import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container, Card, CardBody, CardHeader, Button, Table, Input, Progress, Alert, Spinner, Row, Col,
  Modal, ModalHeader, ModalBody, ModalFooter
} from 'reactstrap';
import BreadCrumb from "../Components/Common/BreadCrumb";
import { setAuthorization, getLoggedinUser } from "../helpers/api_helper";
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');

const PolicyUpload = () => {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [fetchError, setFetchError] = useState(null);
  const [allDocuments, setAllDocuments] = useState([]); // Store all documents for client-side pagination

  useEffect(() => {
    // Set authorization headers before making API calls
    setAuthorization();
    fetchDocuments(1, itemsPerPage);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    return () => {
      setDeleteModalOpen && setDeleteModalOpen(false);
    };
  }, []);

  const getAuthHeaders = () => {
    const authUser = getLoggedinUser();
    if (authUser && authUser.token) {
      return {
        'Authorization': `Bearer ${authUser.token}`,
        'Content-Type': 'application/json'
      };
    }
    return {};
  };

  const getUploadHeaders = () => {
    const authUser = getLoggedinUser();
    if (authUser && authUser.token) {
      return {
        'Authorization': `Bearer ${authUser.token}`
      };
    }
    return {};
  };

  const fetchDocuments = async (page = 1, pageSize = itemsPerPage) => {
    setLoading(true);
    setFetchError(null); // Clear any previous errors
    try {
      // First, try to get all documents (for client-side pagination)
      const res = await axios.get(`${API_BASE}/document/list`, {
        headers: getAuthHeaders(),
        params: { page: 1, pageSize: 1000 } // Get all documents
      });

      let allDocumentsData = [];
      let total = 0;

      // Handle different possible response structures
      if (res.data) {
        // Case 1: Direct array response
        if (Array.isArray(res.data)) {
          allDocumentsData = res.data;
          total = res.data.length;
        }
        // Case 2: Object with rows and count
        else if (res.data.rows && Array.isArray(res.data.rows)) {
          allDocumentsData = res.data.rows;
          total = res.data.count || res.data.total || res.data.totalCount || res.data.rows.length;
        }
        // Case 3: Object with data property
        else if (res.data.data && Array.isArray(res.data.data)) {
          allDocumentsData = res.data.data;
          total = res.data.count || res.data.total || res.data.totalCount || res.data.data.length;
        }
        // Case 4: Object with documents property
        else if (res.data.documents && Array.isArray(res.data.documents)) {
          allDocumentsData = res.data.documents;
          total = res.data.count || res.data.total || res.data.totalCount || res.data.documents.length;
        }
        // Case 5: Single object with results
        else if (res.data.results && Array.isArray(res.data.results)) {
          allDocumentsData = res.data.results;
          total = res.data.count || res.data.total || res.data.totalCount || res.data.results.length;
        }
        // Case 6: Direct object with array-like structure
        else if (typeof res.data === 'object' && res.data.length !== undefined) {
          allDocumentsData = Array.isArray(res.data) ? res.data : [res.data];
          total = Array.isArray(res.data) ? res.data.length : 1;
        }
        // Case 7: Empty or null response
        else {
          allDocumentsData = [];
          total = 0;
        }
      }

      // Store all documents
      setAllDocuments(allDocumentsData);
      setTotalDocuments(total);

      // Apply client-side pagination
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedDocuments = allDocumentsData.slice(startIndex, endIndex);

      setDocuments(paginatedDocuments);

    } catch (err) {
      let msg = 'Failed to fetch documents.';

      if (err.response) {

        if (err.response.status === 404) {
          msg = 'No documents found or the endpoint /document/list does not exist.';
        } else if (err.response.status === 401) {
          msg = 'Unauthorized. Please log in again.';
        } else if (err.response.status === 403) {
          msg = 'Access forbidden. You may not have permission to view documents.';
        } else if (err.response.status === 500) {
          msg = 'Server error. Please try again later.';
        } else if (err.response.data && err.response.data.message) {
          msg = err.response.data.message;
        } else if (err.response.data) {
          msg = JSON.stringify(err.response.data);
        }
      } else if (err.request) {
        msg = 'Network error: Backend server is not reachable.';
      } else if (err.message) {
        msg += ' ' + err.message;
      }

      // Set empty documents array on error
      setDocuments([]);
      setAllDocuments([]);
      setTotalDocuments(0);
      setFetchError(msg);

      // Show error toast
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !title) {
      toast.error('Title and file are required.');
      return;
    }
    setUploading(true);
    setProgress(0);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);

    try {
      await axios.post(`${API_BASE}/document/upload`, formData, {
        headers: {
          ...getUploadHeaders(),
          'Content-Type': undefined // Let browser set the correct Content-Type
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percent);
        },
      });
      toast.success('Document uploaded successfully!');
      setFile(null);
      setTitle('');
      setDescription('');
      fetchDocuments(1, itemsPerPage);
    } catch (err) {
      let errorMsg = 'Upload failed. Please try again.';
      if (err.response) {
        if (err.response.data) {
          if (typeof err.response.data === 'string') {
            errorMsg = err.response.data;
          } else if (err.response.data.msg) {
            errorMsg = err.response.data.msg;
          } else if (err.response.data.message) {
            errorMsg = err.response.data.message;
          } else {
            errorMsg = JSON.stringify(err.response.data);
          }
        } else if (err.response.status) {
          errorMsg = `Error ${err.response.status}: ${err.response.statusText}`;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      toast.error('Failed to upload document.');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDelete = async (id) => {
    setDocToDelete(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;
    try {
      await axios.delete(`${API_BASE}/document/${docToDelete}/delete`, {
        headers: getAuthHeaders()
      });
      toast.success('Document deleted successfully!');
      fetchDocuments(1, itemsPerPage);
    } catch (err) {
      let errorMsg = 'Failed to delete document.';
      if (err.response && err.response.data) {
        errorMsg = err.response.data.message || JSON.stringify(err.response.data);
      } else if (err.message) {
        errorMsg = err.message;
      }
      toast.error('Failed to delete document.');
    } finally {
      setDeleteModalOpen(false);
      setDocToDelete(null);
    }
  };

  const handleView = (doc) => {
    const docId = doc.id || doc._id;
    if (!docId) {
      return;
    }
    const viewUrl = `${API_BASE}/document/${docId}`;
    window.open(viewUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = async (doc) => {
    const docId = doc.id || doc._id;
    const url = `${API_BASE}/document/${docId}/download`;
    const authUser = getLoggedinUser();
    if (!authUser || !authUser.token) {
      toast.error('You must be logged in to download documents.');
      return;
    }
    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${authUser.token}`
        }
      });
      if (!response.ok) {
        let errorMsg = 'Failed to download document.';
        try {
          const errorData = await response.json();
          errorMsg = errorData.msg || errorMsg;
        } catch { }
        throw new Error(errorMsg);
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = doc.fileName || doc.title || doc.name || doc.filename || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      toast.error('Failed to download document.');
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType && fileType.toLowerCase().includes('pdf')) {
      return 'fas fa-file-pdf text-danger';
    } else if (fileType && (fileType.toLowerCase().includes('doc') || fileType.toLowerCase().includes('word'))) {
      return 'fas fa-file-word text-primary';
    } else if (fileType && fileType.toLowerCase().includes('txt')) {
      return 'fas fa-file-alt text-secondary';
    }
    return 'fas fa-file text-muted';
  };

  const toggleDeleteModal = () => setDeleteModalOpen(prev => !prev);

  // Handle page change for client-side pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    const startIndex = (newPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedDocuments = allDocuments.slice(startIndex, endIndex);
    setDocuments(paginatedDocuments);
  };



  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Policy Upload" pageTitle="Policy Upload" />
        <Row className="mb-4 mt-5">
          <Col md={12}>
            <Card className="shadow-sm">
              <CardHeader className="bg-primary text-white">
                <h5 className="mb-0" style={{ color: '#fff', fontWeight: 600 }}>Upload HR Policy Document</h5>
              </CardHeader>
              <CardBody>
                <form onSubmit={handleUpload} className="mb-3">
                  <Row className="align-items-center g-2">
                    <Col md={3} sm={12} className="mb-2 mb-md-0">
                      <Input
                        type="text"
                        placeholder="Title *"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        required
                        style={{ maxWidth: 250 }}
                        disabled={uploading}
                      />
                    </Col>
                    <Col md={4} sm={12} className="mb-2 mb-md-0">
                      <Input
                        type="text"
                        placeholder="Description"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        style={{ maxWidth: 350 }}
                        disabled={uploading}
                      />
                    </Col>
                    <Col md={3} sm={12} className="mb-2 mb-md-0">
                      <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 12 }}>
                        <Input
                          type="file"
                          accept=".pdf,.doc,.docx,.txt"
                          onChange={handleFileChange}
                          disabled={uploading}
                          style={{ maxWidth: 300 }}
                        />
                      </div>
                    </Col>
                    <Col md={2} sm={12} className="mb-2 mb-md-0">
                      <Button
                        className="add-button-margin w-full"
                        type="submit"
                        style={{ backgroundColor: "#5962ef" }}
                        disabled={!file || uploading || !title}
                      >
                        {uploading ? <Spinner size="sm" /> : 'Upload'}
                      </Button>
                    </Col>
                  </Row>
                  {progress > 0 && uploading && <Progress value={progress} className="mt-2" />}
                </form>
                {loading ? (
                  <div className="text-center">
                    <Spinner />
                    <p className="mt-2">Loading documents...</p>
                  </div>
                ) : (
                  <Table bordered responsive hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Title</th>
                        <th>Description</th>
                        <th>File Type</th>
                        <th>Uploaded At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            <div className="d-flex flex-column align-items-center">
                              {fetchError ? (
                                <>
                                  <i className="fas fa-exclamation-triangle mb-3" style={{ fontSize: '3rem', color: '#dc3545' }}></i>
                                  <h5 className="text-danger mb-2">Error Loading Documents</h5>
                                  <p className="text-muted mb-3">{fetchError}</p>
                                  <Button
                                    className="add-button-margin"
                                    color="outline-danger"
                                    size="sm"
                                    style={{ backgroundColor: "#5962ef" }}
                                    onClick={() => fetchDocuments(1, itemsPerPage)}
                                    disabled={loading}
                                  >
                                    <i className="fas fa-sync-alt me-2"></i>
                                    Retry
                                  </Button>
                                  <small className="text-muted">
                                    Check your internet connection and try again.
                                  </small>
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-file-alt mb-3" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                                  <h5 className="text-muted mb-2">No documents found</h5>
                                  <p className="text-muted mb-3">Upload your first HR policy document above to get started.</p>
                                  <Button
                                    className="add-button-margin"
                                    color="outline-primary"
                                    size="sm"
                                    style={{ backgroundColor: "#5962ef" }}
                                    onClick={() => fetchDocuments(1, itemsPerPage)}
                                    disabled={loading}
                                  >
                                    <i className="fas fa-sync-alt me-2"></i>
                                    Refresh Documents
                                  </Button>
                                  <small className="text-muted">
                                    If you've uploaded documents but don't see them here, try refreshing the page.
                                  </small>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        documents.map((doc, index) => (
                          <tr key={doc.id || index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <i className="fas fa-file text-muted me-2" style={{ fontSize: '18px' }}></i>
                                <strong>{doc.title || doc.fileName || 'Untitled'}</strong>
                              </div>
                            </td>
                            <td>{doc.description || '-'}</td>
                            <td>
                              <span className="badge bg-info">
                                {doc.fileName ? doc.fileName.split('.').pop().toUpperCase() : 'Unknown'}
                              </span>
                            </td>
                            <td>
                              {doc.createdAt ? new Date(doc.createdAt).toLocaleString() : '-'}
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <a
                                  href="#"
                                  className="text-decoration-none px-1 py-2"
                                  title="Download Document"
                                  aria-label="Download"
                                  onClick={e => { e.preventDefault(); handleDownload(doc); }}
                                >
                                  <i className="ri-download-2-fill fs-13 text-success p-2 rounded-circle shadow-sm bg-f1"></i>
                                </a>
                                <a
                                  href="#"
                                  className="text-decoration-none px-1 py-2"
                                  title="Delete Document"
                                  aria-label="Delete"
                                  onClick={e => { e.preventDefault(); handleDelete(doc.id); }}
                                >
                                  <i className="ri-delete-bin-6-fill fs-13 text-danger p-2 rounded-circle shadow-sm bg-f1"></i>
                                </a>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <Button
                    className="add-button-margin"
                    color="primary"
                    style={{ backgroundColor: "#5962ef" }}
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  <span>
                    Page {currentPage} of {Math.ceil(totalDocuments / itemsPerPage)} ({documents.length} of {totalDocuments} documents)
                  </span>
                  <Button
                    className="add-button-margin"
                    color="primary"
                    style={{ backgroundColor: "#5962ef" }}
                    disabled={currentPage === Math.ceil(totalDocuments / itemsPerPage) || totalDocuments === 0}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        {/* Delete Confirmation Modal */}
        <Modal isOpen={deleteModalOpen} toggle={toggleDeleteModal || (() => { })}>
          <ModalHeader toggle={toggleDeleteModal || (() => { })}>Confirm Delete</ModalHeader>
          <ModalBody>
            Are you sure you want to delete this document?
          </ModalBody>
          <ModalFooter>
            <Button className="add-button-margin" color="secondary" style={{ backgroundColor: "#5962ef" }} onClick={toggleDeleteModal}>Cancel</Button>
            <Button className="add-button-margin" color="danger" style={{ backgroundColor: "#5962ef" }} onClick={confirmDelete}>Delete</Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default PolicyUpload; 